import { useState, useEffect, useRef } from 'react';

export default function HomePage() {
  const [selectedBets, setSelectedBets] = useState({
    spread: 'Seattle -4.5',
    touchdown: 'K. Walker (SEA)',
    receiving: 'J. Smith-Njigba (SEA)'
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [chartPopupOpen, setChartPopupOpen] = useState(false);
  const [mobileMarketsOpen, setMobileMarketsOpen] = useState(false);

  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const mobileMenuButton = document.querySelector('[aria-label="Toggle menu"]');
      const mobileMenu = document.querySelector('#mobile-menu');
      
      if (mobileMenuOpen && 
          !mobileMenu?.contains(target) && 
          !mobileMenuButton?.contains(target)) {
        setMobileMenuOpen(false);
        setMobileMarketsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
    setMobileMarketsOpen(false);
  };

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-home';
    if (document.getElementById(schemaId)) return;
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'L2L — Sports Prediction Market',
        url: siteUrl,
        description: 'L2L is the zero juice sports prediction market and exchange. Trade NFL, Super Bowl, Olympics, and player props peer-to-peer with no house edge, CFTC regulated, available in all 50 states.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'L2L',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png',
        description: 'Zero juice sports prediction market and exchange.',
        sameAs: [],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What are prediction markets?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Prediction markets let you trade on the outcome of future events. Prices represent the probability the crowd assigns to each outcome. If you think an outcome is more likely than the price suggests, you can profit by buying shares.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do odds work here?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We show odds as "Bet $1 to win $X". For example, "Bet $1 to win $0.91" means risking $1 to potentially win $0.91 profit (plus your $1 back). Lower payouts indicate higher probability outcomes.',
            },
          },
          {
            '@type': 'Question',
            name: "What's a parlay?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A parlay combines multiple predictions into one bet. All selections must win for the parlay to pay out, but the potential payout is much higher than betting each individually.',
            },
          },
        ],
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{background: '#2b2b2b'}}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50 transition-colors duration-300" style={{background: 'rgba(43,43,43,0.97)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            {/* Logo */}
            <a className="flex items-center gap-2 group flex-shrink-0" href="/">
              <div className="h-11 flex items-center justify-center gap-0">
                <div className="h-11 w-44 flex items-center justify-center">
                  <img src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png" alt="L2L Logo" className="h-11 w-auto object-contain" />
                </div>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400">
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                  Home
                </div>
              </a>
              <a href="/march-madness">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>
                  March Madness
                </div>
              </a>
              <a href="/super-bowl-props">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>
                  Super Bowl
                </div>
              </a>


              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white`}
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{background: 'rgba(30,30,30,0.98)'}}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads',      icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400',    path: '/markets/spreads' },
                      { label: 'Money Lines',  icon: 'ri-copper-coin-line',       color: 'text-amber-400',   path: '/markets/money-lines' },
                      { label: 'Futures',      icon: 'ri-calendar-event-line',    color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays',      icon: 'ri-stack-line',             color: 'text-violet-400',  path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line',         color: 'text-cyan-400',    path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <a href="/why-rtj">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>
                  Why L2L
                </div>
              </a>
              <a href="/my-account">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  My Account
                </div>
              </a>
            </div>

            {/* Desktop Right Side */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="relative">
                <div className="relative">
                  <i className="ri-search-line w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                  <input
                    placeholder="Search..."
                    className="w-36 pl-9 pr-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 focus:w-52 transition-all text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer" title="Bet Slip">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Mobile Right Side */}
            <div className="flex lg:hidden items-center gap-3">
              <div className="flex sm:hidden items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10K</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer" title="Bet Slip">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer" 
                aria-label="Toggle menu"
              >
                <i className={`text-xl transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          id="mobile-menu"
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen 
              ? 'max-h-screen opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
          style={{background: 'rgba(30,30,30,0.98)'}}
        >
          <div className="px-4 py-4 border-t border-slate-700/50 backdrop-blur-lg">
            {/* Mobile Search */}
            <div className="mb-4">
              <div className="relative">
                <i className="ri-search-line w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  placeholder="Search markets..."
                  className="w-full pl-9 pr-3 py-3 bg-slate-800/60 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 text-base"
                />
              </div>
            </div>

            {/* Mobile Balance - Only show on smaller screens */}
            <div className="sm:hidden mb-4 p-3 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-wallet-3-line text-teal-400 text-base"></i>
                <span className="font-semibold text-white text-base">Balance: $10,000</span>
              </div>
            </div>

            {/* Mobile Navigation Links */}
            <div className="space-y-1">
              <a href="/" onClick={handleMobileLinkClick}>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer bg-teal-500/20 text-teal-400">
                  <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                  Home
                </div>
              </a>

              <a href="/march-madness" onClick={handleMobileLinkClick}>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-basketball-line w-5 h-5 flex items-center justify-center"></i>
                  March Madness
                </div>
              </a>

              <a href="/super-bowl-props" onClick={handleMobileLinkClick}>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-football-line w-5 h-5 flex items-center justify-center"></i>
                  Super Bowl Props
                </div>
              </a>

              {/* Mobile Markets Dropdown */}
              <div>
                <button
                  onClick={() => setMobileMarketsOpen(!mobileMarketsOpen)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white"
                >
                  <div className="flex items-center gap-3">
                    <i className="ri-bar-chart-2-line w-5 h-5 flex items-center justify-center"></i>
                    Markets
                  </div>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileMarketsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {/* Markets Submenu */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  mobileMarketsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-700/50 pl-4">
                    {[
                      { label: 'All Markets', icon: 'ri-bar-chart-2-line', color: 'text-teal-400', path: '/markets' },
                      { label: 'Spreads', icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400', path: '/markets/spreads' },
                      { label: 'Money Lines', icon: 'ri-copper-coin-line', color: 'text-amber-400', path: '/markets/money-lines' },
                      { label: 'Futures', icon: 'ri-calendar-event-line', color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays', icon: 'ri-stack-line', color: 'text-violet-400', path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line', color: 'text-cyan-400', path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={handleMobileLinkClick}>
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                          <i className={`${item.icon} ${item.color} w-4 h-4 flex items-center justify-center`}></i>
                          {item.label}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <a href="/why-rtj" onClick={handleMobileLinkClick}>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-question-line w-5 h-5 flex items-center justify-center"></i>
                  Why L2L
                </div>
              </a>

              <a href="/my-account" onClick={handleMobileLinkClick}>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-user-line w-5 h-5 flex items-center justify-center"></i>
                  My Account
                </div>
              </a>
            </div>

            {/* Mobile Menu Footer */}
            <div className="mt-6 pt-4 border-t border-slate-700/50">
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-2">Experience the Future of Sports Betting</p>
                <div className="flex justify-center gap-4">
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <i className="ri-shield-check-line text-teal-400"></i>
                    CFTC Regulated
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <i className="ri-global-line text-emerald-400"></i>
                    All 50 States
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #2b2b2b 0%, #333333 50%, #2d2d2d 100%)'}}>
        {/* Hero Section */}
        <section>
          <div className="relative overflow-hidden py-6 px-4">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-teal-500/5"></div>
            <div className="max-w-7xl mx-auto relative z-10">
              <div className="text-center">

                {/* Narrative + Chart */}
                <div className="flex flex-col gap-4 mb-6 max-w-5xl mx-auto">
                  {/* Row 1: Header centered */}
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-tight text-center w-full">
                    Liability to Liquidity: How <span className="text-teal-400">Prediction Markets</span> will Overtake Sportsbooks
                  </h1>

                  {/* Row 2: Subtext left, thumbnail right */}
                  <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="flex-1 text-left">
                      <p className="text-sm md:text-base text-slate-400 leading-relaxed">
                        The market is fading legacy sportsbooks in favor of prediction markets. While the two largest legacy books have seen their valuations cut in half over the last 24 months, prediction market leaders like Kalshi have seen 100x growth. We are witnessing a structural shift where high-velocity liquidity is moving away from house-limited models toward transparent, peer-to-peer exchanges.
                      </p>
                    </div>

                    {/* Clickable Market Convergence Chart */}
                    <div className="w-full md:flex-shrink-0 md:w-auto">
                      <div
                        onClick={() => setChartPopupOpen(true)}
                        className="bg-slate-900/80 rounded-lg border border-slate-700/50 p-3 shadow-xl cursor-pointer hover:border-slate-500 hover:shadow-2xl transition-all group md:w-[280px]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-[100px] sm:w-[120px] h-[64px] sm:h-[72px] flex-shrink-0 overflow-hidden rounded border border-slate-700/30">
                            <img
                              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/ebdc02d91611e19b1884a6c71f6082ad.png"
                              alt="Market Convergence Chart"
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xs font-bold text-white leading-snug mb-1">Prediction Markets could outpace Sports Books this year</h3>
                            <p className="text-xs text-slate-500 mb-1.5">2024–2026</p>
                            <div className="flex items-center gap-1 text-xs text-teal-400 group-hover:text-teal-300 transition-colors">
                              <i className="ri-zoom-in-line"></i>
                              Click to enlarge
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comparison Table */}
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-3 max-w-5xl mx-auto text-left">Sportsbooks vs Prediction Markets</h2>
                <p className="sm:hidden text-xs text-slate-500 mb-2 max-w-5xl mx-auto text-left flex items-center gap-1">
                  <i className="ri-drag-move-line"></i> Scroll right to see full comparison
                </p>
                <div className="max-w-5xl mx-auto mb-5 overflow-hidden rounded-xl border border-slate-700/50 shadow-2xl bg-slate-900/40 backdrop-blur-sm">
                  <div className="overflow-x-auto max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800">
                    <table className="w-full min-w-[640px] text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-slate-800/95 border-b border-slate-700 backdrop-blur-sm">
                          <th className="p-3 text-slate-200 font-bold text-sm w-1/4">Feature</th>
                          <th className="p-3 text-slate-200 font-bold text-sm w-1/3">Sportsbooks</th>
                          <th className="p-3 text-slate-200 font-bold text-sm w-1/3">Prediction Markets</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Structure</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">User vs. Bookmaker (The House)</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Peer-to-Peer (User vs. User)</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-transparent">
                          <td className="p-3 text-slate-400 font-medium text-sm">Juice / Price</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">10% on straights; 30%+ on parlays</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Small trading fees (e.g., &lt;2%)</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Price Clarity</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Confusing -110 American Odds</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Clear e.g. $1 pays $1.95</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-transparent">
                          <td className="p-3 text-slate-400 font-medium text-sm">Payout Logic</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Complicated math to understand profit</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Easy-to-understand payouts</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Bet Limits</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">High-stakes winners are a liability</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">High-stakes winners provide liquidity</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Counterparty Risk</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Accounts can be limited or &quot;slow-played&quot;</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Funds held in regulated escrow</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Market Access</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">State-controlled / Walled Gardens</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Federal (CFTC) / Cross-State Pools</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Tax Classification</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Gambling Income (W-2G)</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Capital Gains/Losses (1099)</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Loss Treatment</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Only deductible against winnings</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Offsets ordinary income (up to $3k/yr)</div>
                          </td>
                        </tr>
                        <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Flexibility</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">&quot;Locked-in&quot; until event completion</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Trade (Buy/Sell) anytime</div>
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors bg-slate-900/30">
                          <td className="p-3 text-slate-400 font-medium text-sm">Price Discovery</td>
                          <td className="p-3 text-sm border-r border-slate-800/50">
                            <div className="text-rose-400 font-medium">Bookmaker sets odds (with hidden vig)</div>
                          </td>
                          <td className="p-3 text-sm bg-emerald-500/5">
                            <div className="text-emerald-400 font-medium">Market-driven efficient pricing</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href="/super-bowl-props" className="w-full sm:w-auto">
                    <button className="w-full sm:w-auto inline-flex items-center justify-center transition-colors bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold px-8 py-3 text-base rounded-lg group cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25">
                      <i className="ri-fire-line mr-2"></i>
                      View Live Markets
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                    </button>
                  </a>
                  <button 
                    onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full sm:w-auto inline-flex items-center justify-center font-medium transition-colors border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-5 py-3 text-sm rounded-lg cursor-pointer whitespace-nowrap gap-2"
                  >
                    <i className="ri-question-line text-lg"></i>
                    How It Works
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Bets Section */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-teal-500/10 border border-teal-500/30 rounded-full text-teal-500 text-sm font-semibold">
                    <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
                    Live Markets
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white">Featured Super Bowl Bets</h2>
              </div>
              <a href="/super-bowl-props">
                <button className="inline-flex items-center justify-center rounded-md text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent h-10 px-4 py-2 text-teal-500 hover:text-teal-400 cursor-pointer whitespace-nowrap">
                  View All
                  <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Bet Card 1 - Spread */}
              <BetCard
                badge="Game"
                badgeColor="amber"
                title="Super Bowl LX Spread"
                description="Game Spread - Main Lines"
                volume24h="$127.4K"
                totalVolume="$520K"
                options={[
                  { name: 'Seattle -4.5', prob: '52%', price: '$1.00', payout: '$1.91', profit: '+$0.91', selected: true },
                  { name: 'New England +4.5', prob: '48%', price: '$1.00', payout: '$2.07', profit: '+$1.07' },
                  { name: 'Seattle -7.5', prob: '35%', price: '$1.00', payout: '$2.84', profit: '+$1.84' },
                  { name: 'New England +7.5', prob: '65%', price: '$1.00', payout: '$1.53', profit: '+$0.53' }
                ]}
                selectedOption={selectedBets.spread}
                onSelectOption={(option) => setSelectedBets({ ...selectedBets, spread: option })}
              />

              {/* Bet Card 2 - Touchdown */}
              <BetCard
                badge="Player"
                badgeColor="cyan"
                title="Anytime Touchdown Scorer"
                description="Anytime Touchdown Scorer"
                volume24h="$89.2K"
                totalVolume="$450K"
                options={[
                  { name: 'K. Walker (SEA)', prob: '64%', price: '$1.00', payout: '$1.55', profit: '+$0.55', selected: true },
                  { name: 'J. Smith-Njigba (SEA)', prob: '51%', price: '$1.00', payout: '$1.95', profit: '+$0.95' },
                  { name: 'R. Stevenson (NE)', prob: '39%', price: '$1.00', payout: '$2.55', profit: '+$1.55' }
                ]}
                selectedOption={selectedBets.touchdown}
                onSelectOption={(option) => setSelectedBets({ ...selectedBets, touchdown: option })}
              />

              {/* Bet Card 3 - Receiving */}
              <BetCard
                badge="Player"
                badgeColor="cyan"
                title="Most Receiving Yards"
                description="Player with Most Receiving Yards in Game"
                volume24h="$54.8K"
                totalVolume="$320K"
                options={[
                  { name: 'J. Smith-Njigba (SEA)', prob: '63%', price: '$1.00', payout: '$1.58', profit: '+$0.58', selected: true },
                  { name: 'Stefon Diggs (NE)', prob: '11%', price: '$1.00', payout: '$9.00', profit: '+$8.00' },
                  { name: 'Hunter Henry (NE)', prob: '6%', price: '$1.00', payout: '$16.56', profit: '+$15.56' },
                  { name: 'Other', prob: '20%', price: '$1.00', payout: '$4.95', profit: '+$3.95' }
                ]}
                selectedOption={selectedBets.receiving}
                onSelectOption={(option) => setSelectedBets({ ...selectedBets, receiving: option })}
              />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/10 border border-teal-500/30 rounded-full text-teal-400 text-base font-medium mb-4">
                <i className="ri-lightbulb-line"></i>
                New to Prediction Markets?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
              <p className="text-slate-400 max-w-2xl mx-auto text-lg md:text-xl">
                Turn your sports knowledge into profit. Prediction markets reward those who can forecast outcomes better than the crowd.
              </p>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              <StepCard
                number="1"
                icon="ri-search-line"
                iconColor="teal"
                title="Browse Markets"
                description="Explore hundreds of prediction markets on NFL games, player props, Super Bowl outcomes, and more."
              />
              <StepCard
                number="2"
                icon="ri-thumb-up-line"
                iconColor="emerald"
                title="Pick Your Side"
                description="Pick one side or the other"
                details={[
                  { label: 'Price:', text: '52% = $0.52 per share.' },
                  { label: 'Payout:', text: '$1.00 for every winning share.' },
                  { label: 'Volume:', text: 'Buy as many shares as you want.' }
                ]}
              />
              <StepCard
                number="3"
                icon="ri-line-chart-line"
                iconColor="cyan"
                title="Trade Anytime"
                description="Prices change as new information emerges. Sell early to lock in profits or cut losses."
              />
              <StepCard
                number="4"
                icon="ri-trophy-line"
                iconColor="amber"
                title="Get Paid"
                description="If your prediction is correct, your shares pay out. Wrong predictions expire worthless."
              />
            </div>

            {/* Understanding Odds */}
            <div className="bg-slate-900/80 rounded-2xl border border-slate-700/80 p-8 mb-16">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Understanding the Odds</h3>
                  <p className="text-slate-400 mb-6 text-base md:text-lg">
                    Our odds show exactly what you stand to win. When you see{' '}
                    <span className="text-teal-400 font-semibold">"52% = $0.52"</span>, it means:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-400 text-sm"></i>
                      </div>
                      <span className="text-slate-300 text-base">
                        You pay <span className="text-white font-medium">$0.52</span> per share
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-400 text-sm"></i>
                      </div>
                      <span className="text-slate-300 text-base">
                        If correct, each share pays <span className="text-teal-400 font-medium">$1.00</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-400 text-sm"></i>
                      </div>
                      <span className="text-slate-300 text-base">
                        Profit per share: <span className="text-white font-medium">$0.48</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-slate-300 text-base">
                        Return on investment: <span className="text-white font-medium">1.92x</span>
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Example Market */}
                <div className="bg-slate-950/80 rounded-xl border border-slate-700/80 p-6">
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-base">
                      Example Market
                    </span>
                  </div>
                  <div className="text-center mb-4">
                    <h4 className="text-white font-semibold text-lg">Super Bowl LX Winner</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/80">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                          <i className="ri-football-line text-teal-400"></i>
                        </div>
                        <span className="text-white font-medium text-base">Seattle Seahawks</span>
                      </div>
                      <div className="text-right">
                        <div className="text-teal-400 font-bold text-base">$0.52</div>
                        <div className="text-sm text-slate-500">Pays $1.92</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/80">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center">
                          <i className="ri-football-line text-cyan-400"></i>
                        </div>
                        <span className="text-white font-medium text-base">New England Patriots</span>
                      </div>
                      <div className="text-right">
                        <div className="text-teal-400 font-bold text-base">$0.48</div>
                        <div className="text-sm text-slate-500">Pays $2.08</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700/50 text-center">
                    <p className="text-sm text-slate-500">Lower price = Higher payout multiplier</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">Common Questions</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <FAQCard
                  question="What are prediction markets?"
                  answer="Prediction markets let you trade on the outcome of future events. Prices represent the probability the crowd assigns to each outcome. If you think an outcome is more likely than the price suggests, you can profit by buying shares."
                />
                <FAQCard
                  question="How do odds work here?"
                  answer='We show odds as "Bet $1 to win $X". For example, "Bet $1 to win $0.91" means risking $1 to potentially win $0.91 profit (plus your $1 back). Lower payouts indicate higher probability outcomes.'
                />
                <FAQCard
                  question="What's a parlay?"
                  answer="A parlay combines multiple predictions into one bet. All selections must win for the parlay to pay out, but the potential payout is much higher than betting each individually."
                />
              </div>
            </div>

            {/* Final CTA */}
            <div className="text-center">
              <a href="/spreads">
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold text-lg rounded-xl shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-rocket-line"></i>
                  Start Exploring Markets
                </button>
              </a>
              <p className="text-slate-500 text-base mt-4">No account required to browse</p>
            </div>
          </div>
        </section>
      </div>

      {/* Chart Popup Modal */}
      {chartPopupOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start gap-3 p-4 sm:p-6 border-b border-slate-700">
              <h2 className="text-base sm:text-2xl font-bold text-white leading-snug">Prediction Markets could outpace Sports Books this year (2024-2026)</h2>
              <button 
                onClick={() => setChartPopupOpen(false)}
                className="text-slate-400 hover:text-white transition-colors flex-shrink-0 mt-0.5"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {/* Large Chart */}
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/94fa7b2574c0d8f28d961fb90caa2e6b.png" 
                  alt="Market Convergence Chart showing FanDuel and DraftKings declining while Kalshi and Polymarket grow exponentially from 2024-2026"
                  className="w-full h-auto rounded-lg border border-slate-700/50"
                />
              </div>
              
              {/* Analysis */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-red-500/30">
                  <h3 className="text-lg font-semibold text-red-400 mb-3">Legacy Sportsbooks Decline</h3>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• FanDuel and DraftKings losing market share</li>
                    <li>• Valuations cut in half over 24 months</li>
                    <li>• Traditional house-edge model becoming obsolete</li>
                    <li>• Regulatory challenges and user restrictions</li>
                  </ul>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-4 border border-teal-500/30">
                  <h3 className="text-lg font-semibold text-teal-400 mb-3">Prediction Markets Rise</h3>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• Kalshi experiencing 100x growth</li>
                    <li>• Polymarket gaining institutional adoption</li>
                    <li>• Peer-to-peer model eliminating house edge</li>
                    <li>• Transparent, market-driven pricing</li>
                  </ul>
                </div>
              </div>
              
              {/* Key Insights */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600/30">
                <h3 className="text-lg font-semibold text-white mb-3">Key Insight</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  We are witnessing a fundamental structural shift in the betting industry. High-velocity liquidity is rapidly moving away from house-limited, opaque sportsbook models toward transparent, peer-to-peer prediction market exchanges. This convergence represents the largest disruption in the sports betting ecosystem since legalization.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bet Card Component
interface BetOption {
  name: string;
  prob: string;
  price: string;
  payout: string;
  profit: string;
  selected?: boolean;
}

interface BetCardProps {
  badge: string;
  badgeColor: 'amber' | 'cyan';
  title: string;
  description: string;
  volume24h: string;
  totalVolume: string;
  options: BetOption[];
  selectedOption: string;
  onSelectOption: (option: string) => void;
}

function BetCard({ badge, badgeColor, title, description, volume24h, totalVolume, options, selectedOption, onSelectOption }: BetCardProps) {
  const badgeClasses = badgeColor === 'amber' 
    ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    : 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';

  const selected = options.find(opt => opt.name === selectedOption) || options[0];

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 shadow-xl overflow-hidden hover:border-slate-500/80 hover:shadow-2xl hover:shadow-teal-500/5 transition-all flex flex-col h-full">
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold tracking-wide border ${badgeClasses}`}>
            <i className={badgeColor === 'amber' ? 'ri-gamepad-line' : 'ri-user-line'}></i>
            {badge}
          </span>
        </div>

        <div className="mb-4">
          <h3 className="text-xl font-bold text-white mb-1 leading-tight">{title}</h3>
          <p className="text-base text-slate-400 line-clamp-2 min-h-[2.5em]">{description}</p>
        </div>

        <div className="flex items-center gap-3 mb-4 p-2.5 bg-white/5 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-1.5">
            <i className="ri-line-chart-line text-teal-500 text-sm"></i>
            <span className="text-xs text-slate-400">24h Vol:</span>
            <span className="text-xs font-bold text-teal-500">{volume24h}</span>
          </div>
          <div className="w-px h-4 bg-slate-700"></div>
          <div className="flex items-center gap-1.5">
            <i className="ri-water-flash-line text-emerald-400 text-sm"></i>
            <span className="text-xs text-slate-400">Volume:</span>
            <span className="text-xs font-bold text-emerald-400">{totalVolume}</span>
          </div>
        </div>

        <div className="flex-1 bg-white/5 rounded-lg p-3 border border-slate-700/50 mb-4 overflow-y-auto max-h-[200px]">
          <div className="space-y-2">
            {options.map((option, idx) => (
              <div
                key={idx}
                onClick={() => onSelectOption(option.name)}
                className={`cursor-pointer p-2 rounded border transition-colors flex justify-between items-center group ${
                  option.name === selectedOption
                    ? 'bg-teal-500/10 border-teal-500/50'
                    : 'bg-white/5 border-slate-700/80 hover:border-slate-500'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`text-base font-semibold truncate ${option.name === selectedOption ? 'text-teal-500' : 'text-slate-300'}`}>
                    {option.name}
                  </div>
                  <div className="text-sm text-slate-500">Prob: {option.prob}</div>
                </div>
                <div className="text-right pl-2 flex flex-col items-end justify-center">
                  <div className="text-xs text-slate-400">Price: {option.price}</div>
                  <div className="text-sm text-slate-300 font-medium">Payout {option.payout}</div>
                  <div className="text-xs text-teal-500">{option.profit}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-auto pt-3 border-t border-slate-700/50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="text-sm text-slate-400">Selected Outcome</div>
              <div className="text-base font-bold text-white truncate max-w-[140px]">{selected.name}</div>
              <div className="text-xs text-slate-400">
                <span>Price: </span>
                <span className="text-white font-semibold">{selected.price}</span>
                <span className="mx-2">•</span>
                <span>Payout: </span>
                <span className="text-teal-500 font-semibold">{selected.payout}</span>
                <span className="text-teal-500 ml-1">{selected.profit}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="inline-flex items-center justify-center text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 rounded-md px-3 sm:px-4 font-bold shadow-lg cursor-pointer whitespace-nowrap bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white shadow-teal-500/25">
                <span className="hidden sm:inline">Add to Slip</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button className="inline-flex items-center justify-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-background hover:text-accent-foreground h-9 rounded-md border-slate-600 hover:bg-slate-800 text-slate-300 px-2 cursor-pointer" title="Grade Wager">
                <i className="ri-scales-3-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step Card Component
interface StepCardProps {
  number: string;
  icon: string;
  iconColor: 'teal' | 'emerald' | 'cyan' | 'amber';
  title: string;
  description: string;
  details?: Array<{ label: string; text: string }>;
}

function StepCard({ number, icon, iconColor, title, description, details }: StepCardProps) {
  const colorClasses = {
    teal: 'bg-teal-500/20 text-teal-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
    amber: 'bg-amber-500/20 text-amber-400'
  };

  return (
    <div className="relative bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-700/80 p-6 hover:border-slate-500/80 hover:shadow-xl transition-all group">
      <div className="absolute -top-3 -left-3 w-8 h-8 bg-slate-800 border border-slate-600 rounded-full flex items-center justify-center text-sm font-bold text-slate-300">
        {number}
      </div>
      <div className={`w-14 h-14 ${colorClasses[iconColor]} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <i className={`${icon} text-2xl`}></i>
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-base leading-relaxed">{description}</p>
      {details && (
        <ul className="mt-3 space-y-2">
          {details.map((detail, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <span className="text-slate-500">•</span>
              <span>
                <span className="text-white font-semibold">{detail.label}</span>{' '}
                <span className="text-slate-400">{detail.text}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// FAQ Card Component
interface FAQCardProps {
  question: string;
  answer: string;
}

function FAQCard({ question, answer }: FAQCardProps) {
  return (
    <div className="bg-slate-900/80 rounded-xl border border-slate-700/80 p-6 hover:border-slate-500/80 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-teal-500/20 rounded-lg flex items-center justify-center">
          <i className="ri-question-line text-teal-400"></i>
        </div>
        <h4 className="text-white font-semibold text-base">{question}</h4>
      </div>
      <p className="text-slate-400 text-base leading-relaxed">{answer}</p>
    </div>
  );
}
